/**
 * Custom hook for managing document filtering state and logic.
 * Uses server-side search when a search query is active (debounced),
 * falls back to local filtering for document type tabs.
 */

import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { SavedDocument } from '@/lib/types';
import type { ProjectConfig } from '@/lib/types';
import { documentsApi } from '@/services/documentsApi';

interface UseDocumentFiltersOptions {
  documents: SavedDocument[];
  projects: ProjectConfig[];
  isCloudEnabled: boolean;
}

interface UseDocumentFiltersResult {
  // URL-based project filter
  selectedProjectId: string | null;
  selectedProject: ProjectConfig | null;

  // Document type filter
  selectedDocType: string | null;

  // Search
  searchQuery: string;

  // Computed data
  documentTypes: string[];
  documentTypeCounts: Record<string, number>;
  projectDocumentCounts: Record<string, number>;
  filteredDocuments: SavedDocument[];

  // View title
  viewTitle: string;

  // Handlers
  handleProjectSelect: (projectId: string | null) => void;
  handleDocTypeSelect: (docType: string | null) => void;
  setSearchQuery: (query: string) => void;
  clearAllFilters: () => void;
}

const SEARCH_DEBOUNCE_MS = 300;

export function useDocumentFilters({
  documents,
  projects,
  isCloudEnabled,
}: UseDocumentFiltersOptions): UseDocumentFiltersResult {
  const STORAGE_KEY = 'systemdox-selected-project';
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedDocType, setSelectedDocType] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [serverSearchResults, setServerSearchResults] = useState<SavedDocument[] | null>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Get selected project from URL
  const selectedProjectId = searchParams.get('project');

  // Restore saved project selection on mount when no URL param is present
  useEffect(() => {
    if (selectedProjectId || projects.length === 0) return;
    const saved = sessionStorage.getItem(STORAGE_KEY);
    if (saved && projects.some((p) => p.id === saved)) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('project', saved);
        return next;
      }, { replace: true });
    }
  }, [projects]); // eslint-disable-line react-hooks/exhaustive-deps

  // Get selected project object
  const selectedProject = useMemo(() => {
    if (!selectedProjectId) return null;
    return projects.find((p) => p.id === selectedProjectId) || null;
  }, [selectedProjectId, projects]);

  // Debounced server-side search
  useEffect(() => {
    if (!searchQuery.trim() || !isCloudEnabled) {
      setServerSearchResults(null);
      return;
    }

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(async () => {
      try {
        const results = await documentsApi.listDocuments({
          search: searchQuery.trim(),
          projectId: selectedProjectId || undefined,
          limit: 500,
        });
        setServerSearchResults(results);
      } catch {
        // Fall back to local filtering on error
        setServerSearchResults(null);
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchQuery, selectedProjectId, isCloudEnabled]);

  // Derive document types from existing documents' templateName field
  const documentTypes = useMemo(() => {
    const types = new Set<string>();
    documents.forEach((doc) => {
      const name = doc.templateName || doc.templateType;
      if (name) types.add(name);
    });
    return Array.from(types).sort();
  }, [documents]);

  // Document counts by display name (computed from loaded documents)
  const documentTypeCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    documents.forEach((doc) => {
      const name = doc.templateName || doc.templateType;
      if (name) {
        counts[name] = (counts[name] || 0) + 1;
      }
    });
    return counts;
  }, [documents]);

  // Document counts by project (server-side for cloud users, local fallback for guests)
  const [serverProjectCounts, setServerProjectCounts] = useState<Record<string, number> | null>(null);

  useEffect(() => {
    if (!isCloudEnabled) {
      setServerProjectCounts(null);
      return;
    }
    let cancelled = false;
    documentsApi.getDocumentTotalsByProject()
      .then((counts) => {
        if (!cancelled) setServerProjectCounts(counts);
      })
      .catch(() => {
        if (!cancelled) setServerProjectCounts(null);
      });
    return () => { cancelled = true; };
  }, [isCloudEnabled, documents.length]);

  const projectDocumentCounts = useMemo(() => {
    if (serverProjectCounts) return serverProjectCounts;
    const counts: Record<string, number> = {};
    for (const project of projects) {
      counts[project.id] = documents.filter((d) => d.projectId === project.id).length;
    }
    return counts;
  }, [projects, documents, serverProjectCounts]);

  // Filter documents by search query and document type
  const filteredDocuments = useMemo(() => {
    // Use server search results when available
    let filtered = (searchQuery.trim() && serverSearchResults) ? serverSearchResults : documents;

    // Filter by document type (match by templateName or templateType)
    if (selectedDocType) {
      filtered = filtered.filter((doc) =>
        (doc.templateName || doc.templateType) === selectedDocType
      );
    }

    // Local fallback search for guests or when server results aren't available yet
    if (searchQuery.trim() && !serverSearchResults) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((doc) => {
        const titleMatch = doc.title.toLowerCase().includes(query);
        const contentMatch = doc.content.toLowerCase().includes(query);
        const templateMatch = (doc.templateName || doc.templateType || '').toLowerCase().includes(query);
        return titleMatch || contentMatch || templateMatch;
      });
    }

    return filtered;
  }, [documents, searchQuery, selectedDocType, serverSearchResults]);

  // Current view title
  const viewTitle = useMemo(() => {
    if (selectedProject) {
      return selectedProject.projectName;
    }
    return 'All Documents';
  }, [selectedProject, selectedDocType]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle project selection
  const handleProjectSelect = useCallback(
    (projectId: string | null) => {
      const newParams = new URLSearchParams();
      if (projectId) {
        newParams.set('project', projectId);
        sessionStorage.setItem(STORAGE_KEY, projectId);
      } else {
        sessionStorage.removeItem(STORAGE_KEY);
      }
      setSearchParams(newParams);
      setSearchQuery('');
      setSelectedDocType(null);
      setServerSearchResults(null);
    },
    [setSearchParams]
  );

  // Handle document type selection
  const handleDocTypeSelect = useCallback((docType: string | null) => {
    setSelectedDocType(docType);
    setSearchQuery('');
    setServerSearchResults(null);
  }, []);

  // Clear all filters
  const clearAllFilters = useCallback(() => {
    sessionStorage.removeItem(STORAGE_KEY);
    setSearchParams(new URLSearchParams());
    setSelectedDocType(null);
    setSearchQuery('');
    setServerSearchResults(null);
  }, [setSearchParams]);

  return {
    selectedProjectId,
    selectedProject,
    selectedDocType,
    searchQuery,
    documentTypes,
    documentTypeCounts,
    projectDocumentCounts,
    filteredDocuments,
    viewTitle,
    handleProjectSelect,
    handleDocTypeSelect,
    setSearchQuery,
    clearAllFilters,
  };
}
